/**
 * Mirrors `GET /api/pipelines/:pipelineId/leads/:leadId/activity`.
 *
 * Separate from `lead-detail.ts` because it follows a different contract: that
 * one follows the adapter's `describeLead`, this one follows the event spine.
 *
 * **`type` is parsed as a plain string, on purpose.** The vocabulary is closed
 * for writes and open for reads — this bundle will meet rows written before it
 * shipped, and rows written by an engine newer than it. Narrowing `type` to the
 * union here would turn "an engine deploy" into "a console deploy is now
 * required", which per-tenant deploys do not give us. The renderer keys a
 * partial map and falls back.
 */

import { z } from 'zod'
import type { LeadId, PipelineId } from '@ludus/core'
import { apiClient } from './client.ts'

const eventSchema = z.object({
  id: z.string(),
  pipelineId: z.string(),
  leadId: z.string().nullable(),
  type: z.string(),
  /** `null` on rows written before the column existed. Never a guess. */
  actor: z.string().nullable(),
  occurredAt: z.number(),
  /** Whatever the engine wrote. A row older than the vocabulary still arrives. */
  payload: z.unknown(),
})

const pageSchema = z.object({
  items: z.array(eventSchema),
  nextCursor: z.string().nullable(),
})

export type LeadActivityEvent = z.infer<typeof eventSchema>
export type LeadActivityPage = z.infer<typeof pageSchema>

export async function fetchLeadActivity(
  pipelineId: PipelineId,
  leadId: LeadId,
  opts: { cursor?: string | null; limit?: number } = {},
): Promise<LeadActivityPage> {
  const { data } = await apiClient.get(`/pipelines/${pipelineId}/leads/${leadId}/activity`, {
    params: {
      ...(opts.cursor ? { cursor: opts.cursor } : {}),
      ...(opts.limit ? { limit: opts.limit } : {}),
    },
  })
  return pageSchema.parse(data)
}
